<template>
  <SectionCard
    title="Screening on title and abstract"
    subtitle="Screen studies based on titles and abstracts. Use this step to narrow down results before full-text screening."
    :display-content="isTitleAndAbstractScreeingCriteriaEnabled"
  >
    <template #header-button>
      <PlanLockGuard>
        <Switch
          :disabled="review.isPlanReadonly.value"
          :model-value="isTitleAndAbstractScreeingCriteriaEnabled"
          @update:model-value="toggleTitleAndAbstractScreeingCriteria"
        >
          <template #icon>
            <component
              :is="
                isTitleAndAbstractScreeingCriteriaEnabled
                  ? ArrowsPointingInIcon
                  : ArrowsPointingOutIcon
              "
              class="w-3 h-auto"
              :class="
                isTitleAndAbstractScreeingCriteriaEnabled
                  ? 'text-green-500'
                  : 'text-dark-neutral-100'
              "
            />
          </template>
        </Switch>
      </PlanLockGuard>
    </template>

    <div class="flex flex-wrap gap-4 items-center">
      <div
        v-for="criterion in titleAndAbstractScreeingCriteria"
        :key="'title-and-abstract-criterion-' + criterion"
        class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-red-600 bg-red-50"
      >
        <span
          v-tooltip="criterion.length > 35 ? criterion : ''"
          class="truncate max-w-[252px]"
          >{{ criterion }}</span
        >
        <PlanLockGuard>
          <button
            :disabled="review.isPlanReadonly.value"
            @click="deleteTitleAndAbstractCriterion(criterion)"
          >
            <XMarkIcon class="w-4 h-4" />
          </button>
        </PlanLockGuard>
      </div>
      <PlanLockGuard v-if="isTitleAndAbstractScreeingCriteriaEnabled">
        <FloatingTextInput
          :disabled="review.isPlanReadonly.value"
          @save="addTitleAndAbstractCriterion($event)"
        >
          <template #activator="{ show }">
            <button
              key=""
              class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
              :disabled="review.isPlanReadonly.value"
              @click.stop="show"
            >
              <PlusIcon class="w-4 h-4" />Add criterion
            </button>
          </template>
        </FloatingTextInput>
      </PlanLockGuard>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import FloatingTextInput from '@app/components/Global/Inputs/FloatingTextInput.vue'
import Switch from '@app/components/Global/Switch.vue'
import useLoading from '@app/composables/use-loading'
import useSnackbar from '@app/composables/use-snackbar'
import { SnackbarState } from '@app/types'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '@app/views/Review/use-review'
import { HttpException } from '@core/exceptions/http.exception'
import { ref, watchEffect } from 'vue'
import PlusIcon from '@app/components/Icons/PlusIcon.vue'
import XMarkIcon from '@app/components/Icons/XMarkIcon.vue'
import SectionCard from '@app/components/Global/SectionCard.vue'
import {
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from '@heroicons/vue/24/outline'
import PlanLockGuard from '../../PlanLockGuard.vue'

const loading = useLoading()
const snackbar = useSnackbar()

const review = injectStrict(ReviewKey)

const titleAndAbstractScreeingCriteria = ref<string[]>([])

const isTitleAndAbstractScreeingCriteriaEnabled = ref(
  (review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria?.length ??
    0) > 0,
)

watchEffect(() => {
  titleAndAbstractScreeingCriteria.value =
    review.entity.value?.plan?.screeningPlan?.titleAndAbstractCriteria ?? []
})

async function toggleTitleAndAbstractScreeingCriteria() {
  if (titleAndAbstractScreeingCriteria.value.length > 0) {
    loading.start()
    try {
      if (titleAndAbstractScreeingCriteria.value.length > 0) {
        await review.disableTitleAndAbstractScreening()
        snackbar.success('Success')
        isTitleAndAbstractScreeingCriteriaEnabled.value = false
      } else {
        await review.enableTitleAndAbstractScreening()
        snackbar.success('Success')
        isTitleAndAbstractScreeingCriteriaEnabled.value = true
      }
    } catch (e) {
      const error = e as HttpException
      if (
        error.response.data.statusCode >= 400 &&
        error.response.data.statusCode < 500
      ) {
        snackbar.show(SnackbarState.ERROR, error.response.data.message)
      } else {
        snackbar.show(
          SnackbarState.ERROR,
          'An error occured while enabling/disabling title and abstract screening, please try again.',
        )
      }
      throw e
    } finally {
      loading.stop()
    }
  } else {
    isTitleAndAbstractScreeingCriteriaEnabled.value =
      !isTitleAndAbstractScreeingCriteriaEnabled.value
    snackbar.success('Success')
  }
}

async function addTitleAndAbstractCriterion(criterion: string) {
  loading.start()
  try {
    await review.addTitleAndAbstractCriterion(criterion)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while adding title and abstract  criterion, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}

async function deleteTitleAndAbstractCriterion(criterion: string) {
  loading.start()
  try {
    await review.deleteTitleAndAbstractCriterion(criterion)
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    if (
      error.response.data.statusCode >= 400 &&
      error.response.data.statusCode < 500
    ) {
      snackbar.show(SnackbarState.ERROR, error.response.data.message)
    } else {
      snackbar.show(
        SnackbarState.ERROR,
        'An error occured while deleting title and abstract criterion, please try again.',
      )
    }
    throw e
  } finally {
    loading.stop()
  }
}
</script>
